import { InventoryService, MovementDateField } from '@services';
import { useStore } from '@stores';
import { getDateRangeFromPreset } from '@utils';
import { debounce } from 'lodash';
import { useListSummary } from '../ListSummary';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useInventoryMovements = () => {
  const { inventoryStore } = useStore();
  const {
    movements,
    movementsTotalCount,
    movementsLoading,
    movementsCurrentPage,
    movementsSkipCount,
    movementsMaxResultCount,
    setMovementsSkipCount,
    setMovementsCurrentPage,
  } = inventoryStore;

  const [name, setName] = useState<string>('');
  const [reference, setReference] = useState<string>('');
  const [partNumber, setPartNumber] = useState<string>('');
  const [nominalCode, setNominalCode] = useState<string>('');
  const [vrm, setVrm] = useState<string>('');
  const [vehicleModel, setVehicleModel] = useState<string>('');
  const [reasons, setReasons] = useState<string[] | undefined>();
  const [supplierIds, setSupplierIds] = useState<string[] | undefined>();
  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState<string>('');
  const [dateField, setDateField] = useState<MovementDateField>('MovementDate');
  const [datePreset, setDatePreset] = useState<string | undefined>();
  const [customFrom, setCustomFrom] = useState<string | undefined>();
  const [customTo, setCustomTo] = useState<string | undefined>();

  const { dateFrom, dateTo } = useMemo(() => {
    if (datePreset === 'custom') {
      return { dateFrom: customFrom, dateTo: customTo };
    }
    const resolved = getDateRangeFromPreset(datePreset);
    return { dateFrom: resolved.startAt, dateTo: resolved.endAt };
  }, [datePreset, customFrom, customTo]);

  useEffect(() => {
    inventoryStore.getAllMovements({
      name: name || undefined,
      reference: reference || undefined,
      partNumber: partNumber || undefined,
      nominalCode: nominalCode || undefined,
      vrm: vrm || undefined,
      vehicleModel: vehicleModel || undefined,
      reasons,
      supplierIds,
      supplierInvoiceNumber: supplierInvoiceNumber || undefined,
      dateField,
      dateFrom,
      dateTo,
      maxResultCount: movementsMaxResultCount,
      skipCount: movementsSkipCount,
    });
  }, [name, reference, partNumber, nominalCode, vrm, vehicleModel, reasons, supplierIds, supplierInvoiceNumber, dateField, dateFrom, dateTo, movementsMaxResultCount, movementsSkipCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Totals over the whole filtered set: the same filters as the table, none of its paging.
  const { summary: totals, loading: totalsLoading, refresh: refreshSummary } = useListSummary(
    () =>
      InventoryService.getMovementSummary({
        name: name || undefined,
        reference: reference || undefined,
        partNumber: partNumber || undefined,
        nominalCode: nominalCode || undefined,
        vrm: vrm || undefined,
        vehicleModel: vehicleModel || undefined,
        reasons,
        supplierIds,
        supplierInvoiceNumber: supplierInvoiceNumber || undefined,
        dateField,
        dateFrom,
        dateTo,
        maxResultCount: 1,
        skipCount: 0,
      }),
    [name, reference, partNumber, nominalCode, vrm, vehicleModel, reasons, supplierIds, supplierInvoiceNumber, dateField, dateFrom, dateTo]
  );

  const resetPage = () => {
    setMovementsSkipCount(0);
    setMovementsCurrentPage(1);
  };

  const handlePageChange = (pagination: any) => {
    inventoryStore.handleMovementsPageChange(pagination);
  };

  const handleNameSearch = useMemo(
    () =>
      debounce((text: string) => {
        setName(text);
        resetPage();
      }, 500),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleReferenceSearch = useMemo(
    () =>
      debounce((text: string) => {
        setReference(text);
        resetPage();
      }, 500),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handlePartNumberSearch = useMemo(
    () =>
      debounce((text: string) => {
        setPartNumber(text);
        resetPage();
      }, 500),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleNominalCodeSearch = useMemo(
    () =>
      debounce((text: string) => {
        setNominalCode(text);
        resetPage();
      }, 500),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleVrmChange = useMemo(
    () =>
      debounce((text: string) => {
        setVrm(text);
        resetPage();
      }, 500),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleVehicleModelChange = useMemo(
    () =>
      debounce((text: string) => {
        setVehicleModel(text);
        resetPage();
      }, 500),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleSupplierInvoiceNumberSearch = useMemo(
    () =>
      debounce((text: string) => {
        setSupplierInvoiceNumber(text);
        resetPage();
      }, 500),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleReasonFilter = useCallback((values: string[] | undefined) => {
    setReasons(values && values.length ? values : undefined);
    resetPage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSupplierFilter = useCallback((values: string[] | undefined) => {
    setSupplierIds(values && values.length ? values : undefined);
    resetPage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateFieldChange = useCallback((field: MovementDateField) => {
    setDateField(field);
    resetPage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDatePresetChange = useCallback((preset: string | undefined) => {
    setDatePreset(preset || undefined);
    if (preset !== 'custom') {
      setCustomFrom(undefined);
      setCustomTo(undefined);
    }
    resetPage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCustomDateRange = useCallback((from: string | undefined, to: string | undefined) => {
    setCustomFrom(from);
    setCustomTo(to);
    resetPage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAllForExport = useCallback(async () => {
    const result = await InventoryService.getAllMovements({
      name: name || undefined,
      reference: reference || undefined,
      partNumber: partNumber || undefined,
      nominalCode: nominalCode || undefined,
      vrm: vrm || undefined,
      vehicleModel: vehicleModel || undefined,
      reasons,
      supplierIds,
      supplierInvoiceNumber: supplierInvoiceNumber || undefined,
      dateField,
      dateFrom,
      dateTo,
      maxResultCount: movementsTotalCount || 10000,
      skipCount: 0,
    });
    return result.items;
  }, [name, reference, partNumber, nominalCode, vrm, vehicleModel, reasons, supplierIds, supplierInvoiceNumber, dateField, dateFrom, dateTo, movementsTotalCount]);

  const refresh = useCallback(() => {
    inventoryStore.getAllMovements({
      name: name || undefined,
      reference: reference || undefined,
      partNumber: partNumber || undefined,
      nominalCode: nominalCode || undefined,
      vrm: vrm || undefined,
      vehicleModel: vehicleModel || undefined,
      reasons,
      supplierIds,
      supplierInvoiceNumber: supplierInvoiceNumber || undefined,
      dateField,
      dateFrom,
      dateTo,
      maxResultCount: movementsMaxResultCount,
      skipCount: movementsSkipCount,
    });
    refreshSummary();
  }, [name, reference, partNumber, nominalCode, vrm, vehicleModel, reasons, supplierIds, supplierInvoiceNumber, dateField, dateFrom, dateTo, movementsMaxResultCount, movementsSkipCount, refreshSummary]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    loading: movementsLoading,
    dataSource: movements,
    totalCount: movementsTotalCount,
    currentPage: movementsCurrentPage,
    maxResultCount: movementsMaxResultCount,
    datePreset,
    dateField,
    customFrom,
    customTo,
    reasons,
    handlePageChange,
    handleNameSearch,
    handleReferenceSearch,
    handlePartNumberSearch,
    handleNominalCodeSearch,
    handleVrmChange,
    handleVehicleModelChange,
    handleSupplierInvoiceNumberSearch,
    handleReasonFilter,
    handleSupplierFilter,
    handleDateFieldChange,
    handleDatePresetChange,
    handleCustomDateRange,
    refresh,
    fetchAllForExport,
    totals,
    totalsLoading,
  };
};
